import React, {useRef, useEffect, useState} from "react";
import { useDispatch,useSelector } from "react-redux";
import { Box, IconButton, Paper, styled, TextField, CircularProgress, Tooltip, Button, Stack} from "@mui/material";
import Tree from 'rc-tree';
import { useTranslation } from "react-i18next";
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { api } from '../../redux/api';
import "../../assets/css/rcTree.css";
import { debounce } from "lodash";
import LightTooltip from "../../commonComponents/LightTooltip";
import aiIcon from '../../assets/icons/ask_ai_icon.png'

const LeftTreePanel = styled(Paper)(({ theme }) => ({
    ["&"]: {
        padding: '5px',
        flexDirection: 'column',
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 0,
        borderRight: 0
    },
    "& .MuiInputBase-root > .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e0e0e0",
        borderWidth: '1px'
    },
    "& .MuiInputBase-inputSizeSmall": {
        backgroundColor: theme.palette.background.paper,
        fontSize: '14px !important',
        width: "100%",
        lineHeight: '22px',
    },
}));

const styles = {
    searchInput: {
        borderRadius: '5px',
        marginBottom: '5px',
        ['& .MuiInputBase-root']: {
            padding: 0
        }
    },
    treeContainer: {
        height: '100%',
        boxSizing: "border-box",
        width: "100%",
        overflow: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: "5px",
    },
}

function HelpTree(props) {
    const dispatch = useDispatch();
    const treeRef = useRef();
    const inputRef = useRef();
    const { account} = useSelector(state =>state.queryParams)

    const [keyUpDownNav, setKeyUpDownNav] = useState(false);
    const { t } = useTranslation();
    const onExpand = (expandedKeys) => {
        props.setExpandedKeys &&
        props.setExpandedKeys(expandedKeys);
    };

    const onSelect = (selectedKeys, info) => {
        // props.setSelectedKeys(selectedKeys);
        props.setSelectedNode(info.node);
        
        // If first node (ht-0) is selected, open chatbot 
        if(info.node.key === 'ht-0'){
            // Clear selected node and search doc when opening chatbot
            // if(props.setSelectedNode){
            //     props.setSelectedNode(false);
            // }
            if(props.setSearchDocument){
                props.setSearchDocument(null);
            }
            if(props.setAiHelpOpen){
                props.setAiHelpOpen(true);
            }
            return;
        }
        
        // Close chatbot when any other tree node is selected
        if(props.setAiHelpOpen){
            props.setAiHelpOpen(false);
        }
        if(props.enableSearchTree){
            dispatch(
                api.endpoints.searchHelpDoc.initiate({ 
                    keyword: props?.highlightKeyword, 
                    searchString: info.node.path },{forceRefetch:true})
              ).then((response) => {
                if(response?.isSuccess){
                    props.setSearchDocument(response?.data?.documentation);
                }
              });
        }
    };

    const Icon = (node) => {
        return !node?.data?.children ? 
        <span id={node.data.key} className="DOCUMENT"></span> : 
        <span id={node.data.key} className="RECORD"></span>
    }

    const switcherIcon = (obj) => {
        if (obj.isLeaf) return;
        if (obj.expanded) return <ArrowDropDownIcon />
        else return <ArrowRightIcon sx={{color: '#666666'}}/>
    }

    const onSearchClearKeywords = () => {
        props.setLoading(true);
        props.setEnableSearchTree(false);
        props.setSearchDocument(null);
        props.setHighlightKeyword('');
        props.setSelectedNode(false);
        props.setExpandedKeys([]);
        inputRef.current.querySelector('input').value = "";
        dispatch(api.endpoints.getHelpTreeList.initiate({account:account},{forceRefetch: true})).then(() => {
            setTimeout(() => {
                props.setLoading(false);
            }, 300)
        });
    }

    const onSearchString = debounce((event) => {
        if(event.target.value){
            if(props.setAiHelpOpen){
                props.setAiHelpOpen(false);
            }
            dispatch(api.endpoints.getSearchHelpDocs.initiate({
                keyword: event.target.value
            }, {forceRefetch: true})).then(response => {
                if(response?.isSuccess){
                    props.setEnableSearchTree(true);
                }
                props.setLoading(false);  
            });
        }
        else{
            onSearchClearKeywords();
        }
        props.setHighlightKeyword(event.target.value);  
    }, 1000);

    const handleKeyDown = (event) => {
        if(keyUpDownNav && props?.selectedKeys?.length > 0){
            const currentIndex = props?.collectedKeys?.indexOf(props?.selectedKeys?.[0]);
            
            if(event.key === "ArrowUp"){
                const nextNodeKey = props?.collectedKeys[currentIndex - 1]
                const nextNode = props?.collectedNodes[nextNodeKey];
                
                // If first node (ht-0) is selected, open chatbot
                if(nextNodeKey === 'ht-0'){
                    const ht0Node = props?.collectedNodes?.['ht-0'] || props?.treeData?.[0];
                    if(props.setSelectedNode && ht0Node){
                        props.setSelectedNode(ht0Node);
                    }
                    if(props.setSearchDocument){
                        props.setSearchDocument(null);
                    }
                    if(props.setAiHelpOpen){
                        props.setAiHelpOpen(true);
                    }
                    return;
                }
                
                let expandedKeysArr = [];
                props.setSelectedNode(nextNode)
                // Close chatbot when navigating to a node
                if(props.setAiHelpOpen){
                    props.setAiHelpOpen(false);
                }
                if(props?.expandedKeys?.indexOf(nextNodeKey) === -1)
                    expandedKeysArr = [...props?.expandedKeys, nextNodeKey];
                else
                    expandedKeysArr = props?.expandedKeys;
                props.setExpandedKeys(expandedKeysArr)
            
                if(props.enableSearchTree){
                    dispatch(
                        api.endpoints.searchHelpDoc.initiate({ 
                            keyword: props?.highlightKeyword, 
                            searchString: nextNode.path },{forceRefetch:true})
                      ).then((response) => {
                        if(response?.isSuccess){
                            props.setSearchDocument(response?.data?.documentation);
                        }
                      });
                }
            }
            else if(event.key === "ArrowDown"){
                const nextNodeKey = props?.collectedKeys[+currentIndex + 1];
                const nextNode = props?.collectedNodes[nextNodeKey];
                
                // If first node (ht-0) is selected, open chatbot
                if(nextNodeKey === 'ht-0'){
                    const ht0Node = props?.collectedNodes?.['ht-0'] || props?.treeData?.[0];
                    if(props.setSelectedNode && ht0Node){
                        props.setSelectedNode(ht0Node);
                    }
                    if(props.setSearchDocument){
                        props.setSearchDocument(null);
                    }
                    if(props.setAiHelpOpen){
                        props.setAiHelpOpen(true);
                    }
                    return;
                }
                
                let expandedKeysArr = [];
                props.setSelectedNode(nextNode);
                // Close chatbot when navigating to a node
                if(props.setAiHelpOpen){
                    props.setAiHelpOpen(false);
                }
                if(props?.expandedKeys?.indexOf(nextNodeKey) === -1)
                    expandedKeysArr = [...props?.expandedKeys, nextNodeKey];
                else
                    expandedKeysArr = props?.expandedKeys;
                props.setExpandedKeys(expandedKeysArr)
                
                if(props.enableSearchTree){
                    dispatch(
                        api.endpoints.searchHelpDoc.initiate({ 
                            keyword: props?.highlightKeyword, 
                            searchString: nextNode.path },{forceRefetch:true})
                      ).then((response) => {
                        if(response.isSuccess){
                            props.setSearchDocument(response.data.documentation);
                        }
                      });
                }
            }
        }
    }

    const enableKeyUpDown = () => setKeyUpDownNav(true);
    const disableKeyUpDown = () => setKeyUpDownNav(false);

    useEffect(() => {
        document.body?.addEventListener('keydown', handleKeyDown);
        treeRef.current.addEventListener('focus', enableKeyUpDown);
        treeRef.current.addEventListener('blur', disableKeyUpDown);

        return () => {
            document.body?.removeEventListener('keydown', handleKeyDown);
            treeRef.current?.removeEventListener('focus', enableKeyUpDown);
            treeRef.current?.removeEventListener('blur', disableKeyUpDown);
        }
    },[props?.selectedKeys, 
       props?.collectedKeys, 
       props?.collectedNodes, 
       props?.expandedKeys,
       treeRef?.current])

    return (
        <LeftTreePanel variant="outlined" sx={{display: `${props.collapsed ? 'none' : 'flex'}`}}>
            <Stack direction="row" spacing={1} sx={{marginBottom: '5px'}}>
                         {/* <TextField 
                    size="small" 
                    variant="outlined" 
                    autoComplete="off" 
                    InputProps={{
                    endAdornment: (
                        !props.enableSearchTree ? 
                        
                        <LightTooltip title={t('TYPE_TO_SEARCH_HELP_DOCUMENTS')}>
                            <IconButton>
                                <SearchIcon />
                            </IconButton>
                        </LightTooltip> :
                        <LightTooltip title={t('CLEAR_SEARCH')}>
                            <IconButton onClick={onSearchClearKeywords}>
                                <ClearIcon />
                            </IconButton>
                        </LightTooltip>
                    ),
                    }} 
                    onChange={(event) => {
                        props.setLoading(true);
                        onSearchString(event)
                    }}
                    ref={inputRef}
                    // value={props?.highlightKeyword}
                    placeholder={t('SRCH')} 
                    sx={styles.searchInput}
                    fullWidth />
                    <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => {
                            // Set selected node to ht-0 (Documentation) when opening chatbot
                            const ht0Node = props?.collectedNodes?.['ht-0'] || props?.treeData?.[0];
                            if(props.setSelectedNode && ht0Node){
                                props.setSelectedNode(ht0Node);
                            }
                            if(props.setSearchDocument){
                                props.setSearchDocument(null);
                            }
                            props.setAiHelpOpen(true);
                        }}
                        sx={{ minWidth: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <LightTooltip title={t('AI_HELP')}>
                            <img src={aiIcon} alt="ask-AI" style={{width: 24, height: 24}}   />
                        </LightTooltip>
                    </Button>  */}

            <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={() => {
                    // Set selected node to ht-0 (Documentation) when opening chatbot
                    const ht0Node = props?.collectedNodes?.['ht-0'] || props?.treeData?.[0];
                    if(props.setSelectedNode && ht0Node){
                        props.setSelectedNode(ht0Node);
                    }
                    props.setAiHelpOpen(true);
                }}
                sx={{
                    marginBottom: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    textTransform: 'none'
                }}
            >
                <img src={aiIcon} alt="ask-AI" style={{width: 20, height: 20}} />
                {t('AI_HELP')}
            </Button>
            </Stack>
            <Box sx={styles.treeContainer} ref={treeRef} tabIndex="-1">
                {!props?.loading ? (
                props?.treeData?.length > 0 ? 
                <Tree
                    focusable
                    showLine
                    autoFocus
                    onExpand={onExpand}
                    onSelect={onSelect}
                    treeData={props.treeData}
                    icon={Icon}
                    selectedKeys={props.selectedKeys}
                    {...(props.expandedKeys ? {
                        expandedKeys: props?.expandedKeys
                    } :{})}
                    // expandedKeys={props.expandedKeys}
                    // showIcon
                    switcherIcon={switcherIcon}
                    selectable
                /> :
                 <div className="panel-no-data">{t("LBL_NO_IN_SEL")}</div>) : 
                 <div className='loading-overlay'>
                    <div className='flex-column align-center'>
                        <CircularProgress color="primary" />
                    </div>
                </div>}
            </Box>
        </LeftTreePanel>
    )
}
  
export default React.memo(HelpTree);